import {
  AbstractType,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  InjectionToken,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  Type,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  find,
  flatten,
  get,
  head,
  isArray,
  isEmpty,
  isFunction,
  join,
  last,
  map,
  orderBy,
  pick,
  set,
  some,
  startCase,
  values,
  keyBy,
  trim,
} from 'lodash';
import {
  Observable,
  observableCombineLatest,
  observableMerge,
  observableOf,
  rx,
  Subscription,
} from '@shared/rx';
import {
  DataSource,
  EntityServiceProvider,
  PermissionService,
  ScanningService,
  StateService,
} from '@shared/services';
import {
  artificialInputWatcher,
  asObservable,
  isTextPresentInSelectOptions,
} from '@shared/utils/common-utils';
import {
  DEFAULT_ARTIFICIAL_INPUT_THRESHOLD,
  SERVER_REQUEST_DELAY_MS,
} from '@config/global-constants';
import { APIActions, UiActions } from '@store/actions';
import {
  AnyObject,
  IBaseEntityType,
  IFieldPreferences,
  IFormRules,
  IModalDialogOptions,
  IPopulateSearchValueDefinition,
  SchemaNode,
  SelectOption,
  ISortOptions,
  IArtificialInputWatcherOption,
  EInputEvent,
} from '@shared/types';
import {
  BTN_CANCEL,
  BTN_SAVE,
} from '@text-content/button-templates/button-templates-content';
import { onControlValueChanged } from '@shared/rx-operators';
import { DropdownMenuService } from '@geniuspos/ui-shared/modules/xdm-shared-service/dropdown-menu/dropdown-menu.service';
import {
  HorizontalPosition,
  VerticalPosition,
} from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/types/dropdown.type';
import { XuxInputEntitySelectMenuComponent } from './xux-input-entity-select-menu/xux-input-entity-select-menu.component';
import { BehaviorSubject, Subject } from 'rxjs';
import {
  Condition,
  InfoBlock,
} from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/types/common.type';
import { lazyInject } from '@app/containers/base/decorators';

@Component({
  selector: 'xux-input-entity-select',
  templateUrl: 'xux-input-entity-select.component.html',
  styleUrls: ['./xux-input-entity-select.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class XuxInputEntitySelectComponent
  implements OnChanges, OnInit, OnDestroy, AfterViewInit
{
  @ViewChild('dropdownTarget', { static: true })
  public dropdownTarget: ElementRef;
  @ViewChild('defaultFocusElement', { static: true })
  public defaultFocusElement: ElementRef;
  @Input() public field: IFieldPreferences = {};
  @Input() public shouldShowDropdown: boolean = true;
  @lazyInject(ScanningService) public scanningService: ScanningService;

  public searchControl: FormControl = new FormControl('');
  public control: FormControl;

  public dataSource: DataSource;
  public defaultSelectedValue: Condition;
  public busy$: Observable<boolean>;

  // ENTITY_TYPE from the entity config
  public entityType: string;
  // field for representing entity name for a user
  public entityName: string;

  public canAdd: boolean = false;
  public hasAddPermission: boolean = false;
  public isMenuAlreadyOpen: boolean = false;

  public entitySchema: SchemaNode;

  public entitySchema$: Observable<SchemaNode>;
  public selectOptions$: BehaviorSubject<SelectOption[]> = new BehaviorSubject(
    [],
  );
  public selectedItemText$: Observable<string>;
  public insertableVisibility$: BehaviorSubject<boolean> = new BehaviorSubject(
    false,
  );
  public canSearch: boolean = true;
  public resetOnEmptySearch: boolean = true;
  public pattern: RegExp = null;
  public emptyOptionText: string = '';
  public emptyOptionValue: string = null;
  public enableClientFiltering: boolean = false;
  public minClientFilteringCharLength: number = 2;
  public isModal: boolean = false;
  public placeholder$: Observable<string> = observableOf('');
  public cancelSubscription$ = new Subject<void>();

  private infoBlockError$: BehaviorSubject<InfoBlock> =
    new BehaviorSubject<InfoBlock>(null);
  private subscriptions: Subscription[] = [];
  private openOnInit: boolean = false;

  protected artificialInputCheck:
    | {
        artificialInputThreshold?: number;
      }
    | boolean = false;

  protected get artificialInputThreshold() {
    return get(
      this.artificialInputCheck,
      'artificialInputThreshold',
      DEFAULT_ARTIFICIAL_INPUT_THRESHOLD,
    );
  }

  // eslint-disable-next-line max-params
  public constructor(
    private readonly entityServiceProvider: EntityServiceProvider,
    private readonly uiActions: UiActions,
    private readonly apiActions: APIActions,
    private readonly stateService: StateService,
    private readonly permissionService: PermissionService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    public readonly dropdownMenuService: DropdownMenuService,
    public injector: Injector,
  ) {}

  public excludeSelectItems: Function = () => [];

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.dataSource =
      get(this.field, ['widgetData', 'dataSource']) || this.createDataSource();
    this.defaultSelectedValue = get(this.field, [
      'widgetData',
      'dataSourceOptions',
      'defaultSelectedValue',
    ]);
    this.entityType = get(
      this.dataSource,
      ['service', 'config', 'ENTITY_TYPE'],
      get(
        this.dataSource,
        ['service', 'config', 'entityNameToCheckPermissions'],
        '',
      ),
    );
    this.entityName = get(this.field, 'title', startCase(this.entityType));
    this.busy$ = this.dataSource.busy.asObservable();
    this.control = get<IFieldPreferences, any, FormControl>(
      this.field,
      'control',
      new FormControl(''),
    );
    this.canAdd = get<IFieldPreferences, any, boolean>(
      this.field,
      ['widgetData', 'canAdd'],
      this.canAdd,
    );
    this.canSearch = get(
      this.field,
      ['widgetData', 'canSearch'],
      this.canSearch,
    );
    this.openOnInit = get(
      this.field,
      ['widgetData', 'openOnInit'],
      this.openOnInit,
    );
    this.pattern = get(this.field, ['widgetData', 'pattern'], null);
    this.isModal = get(this.field, ['widgetData', 'isModal'], false);

    const placeholderSource =
      get(this.field, ['widgetData', 'placeholder']) ??
      get(this.field, 'placeholder', '');
    this.placeholder$ = asObservable(placeholderSource);

    this.emptyOptionText = get(
      this.field.widgetData,
      'emptyOptionText',
      this.emptyOptionText,
    );
    this.emptyOptionValue = get(
      this.field.widgetData,
      'emptyOptionValue',
      this.emptyOptionValue,
    );
    this.resetOnEmptySearch = get(
      this.field.widgetData,
      'resetOnEmptySearch',
      this.resetOnEmptySearch,
    );
    this.excludeSelectItems = get(
      this.field.widgetData,
      'excludeSelectItems',
      this.excludeSelectItems,
    );
    this.enableClientFiltering = get(
      this.field.widgetData,
      'enableClientFiltering',
      this.enableClientFiltering,
    );
    this.minClientFilteringCharLength = get(
      this.field.widgetData,
      'minClientFilteringCharLength',
      this.minClientFilteringCharLength,
    );
    if ('field' in changes) this.updateInfoBlockValue();
  }

  public ngOnInit(): void {
    this.hasAddPermission = this.permissionService.canAdd(this.entityType);
    this.artificialInputCheck = get(
      this.field,
      'widgetData.artificialInputCheck',
      false,
    );
    if (this.canAdd && this.hasAddPermission) {
      this.entitySchema$ = this.stateService.select([
        'entities',
        this.entityType,
        'schema',
      ]);
      this.apiActions.ensureEntitySchemaLoaded(this.entityType);
    }

    if (!get(this.field, 'widgetData.lazy')) {
      void this.dataSource.nextPage();
    }

    this.subscriptions.push(
      observableCombineLatest([
        this.dataSource.items.asObservable(),
        onControlValueChanged(this.control),
      ])
        .pipe(
          rx.map(([items, selected]) => {
            if (this.defaultSelectedValue && !isEmpty(items) && !selected) {
              const defaultValue = find(items, this.defaultSelectedValue);
              if (defaultValue) {
                this.control.setValue(
                  this.dataSource.getItemValue(defaultValue),
                );
              }
            }
            const objectOfSelectedItems = keyBy(this.excludeSelectItems());
            const filteredItems = items.filter(
              (item) => !objectOfSelectedItems[item.entity_id],
            );

            const options: SelectOption[] = map(filteredItems, (item) => ({
              text: this.dataSource.getItemText(item),
              value: this.dataSource.getItemValue(item),
              selected: this.dataSource.getItemValue(item) === selected,
            }));
            const sortedOptions = this.adjustOrderOptionsMapper(options);

            return this.prependEmptyOption(sortedOptions);
          }),
        )
        .subscribe((selectOptions: SelectOption[]) => {
          this.selectOptions$.next(selectOptions);
          this.insertableVisibility$.next(
            !isTextPresentInSelectOptions(
              selectOptions,
              `${this.searchControl.value}`,
            ),
          );

          // To open select we need to check whether data was already retrieved
          // (select start fetching data only on dropdown shown) so we trigger first load by running "show"
          // and then actually open dropdown.
          if (this.openOnInit) {
            // Simple check whether data already fetched
            // (even if it is "0" or "false" nothing wrong will happen if we try to open select twice ).
            const isOptionsEmpty = !!get(last(selectOptions), 'value');

            if (isOptionsEmpty) {
              this.openOnInit = false;
            }
            this.displayMenu();
          }
          this.changeDetectorRef.markForCheck();
        }),
    );

    this.subscriptions.push(
      this.searchControl.valueChanges
        .pipe(
          rx.tap((text) => {
            if (text === '' && this.resetOnEmptySearch) {
              this.setValue(null);
            }
            if (this.hasPatternAndDoNotPassIt(text)) {
              this.searchControl.setValue(
                this.removeCharactersDoNotPassPattern(text),
              );
            }
          }),
          rx.debounceTime(SERVER_REQUEST_DELAY_MS),
        )
        .subscribe((text) => {
          let textValue = text || '';
          if (this.hasPatternAndDoNotPassIt(text)) {
            textValue = this.removeCharactersDoNotPassPattern(text);
          }
          this.searchControlUserInput(textValue);
          this.changeDetectorRef.markForCheck();
        }),

      onControlValueChanged(this.control).subscribe((value) =>
        this.dataSource.setSelection([value]),
      ),

      this.dataSource.selectedItems
        .asObservable()
        .pipe(rx.filter((selection) => isArray(selection)))
        .subscribe((selection) => {
          if (this.field?.widgetData?.focusOnClick && this.isValueEmpty()) {
            this.searchControl.setValue('', { emitEvent: false });
            setTimeout(() => this.defaultFocusElement.nativeElement.focus());

            return;
          }
          const selectedItem = head(selection);
          const controlText = selectedItem
            ? this.dataSource.getItemText(selectedItem)
            : '';
          this.searchControl.setValue(controlText, { emitEvent: false });
          this.changeDetectorRef.markForCheck();
        }),
      this.dropdownMenuService.clearContainer$.subscribe(() => {
        this.isMenuAlreadyOpen = false;
        this.changeDetectorRef.markForCheck();
      }),
    );
  }

  public async displayMenu(event?: Event): Promise<void> {
    if (!this.shouldShowDropdown) return;
    if (this.isMenuAlreadyOpen) {
      this.dropdownMenuService.clearContainer();

      return;
    }
    await this.dropDownShown();
    this.dropdownMenuService.createComponent(
      XuxInputEntitySelectMenuComponent,
      {
        callBackFunctions: {
          onScroll: this.onScroll.bind(this),
          setValue: this.setValue.bind(this),
          trackByValue: this.trackByValue.bind(this),
          addNewItem: this.addNewItem.bind(this),
        },
        optionFlags: {
          busyObs: this.busy$,
          dataSourceEmptyValue: this.dataSource?.empty?.value,
          canAdd: this.canAdd,
          canAddWidgetData: this.field?.widgetData?.canAdd,
          hasAddPermission: this.hasAddPermission,
          insertable: this.field?.widgetData?.insertable,
          insertableVisibility$: this.insertableVisibility$,
          hideShowNoMatches: this.field?.widgetData?.hideShowNoMatches,
        },
        selectOptions$: this.selectOptions$,
        entityName: this.entityName,
        searchControl: this.searchControl,
      },
      this.dropdownTarget.nativeElement,
      HorizontalPosition.CENTER,
      VerticalPosition.BOT,
      HorizontalPosition.LEFT,
      true,
    );
    this.isMenuAlreadyOpen = true;
    event?.stopImmediatePropagation();
  }

  public ngOnDestroy(): void {
    this.dataSource.selectedItems.next([]);
    this.dataSource.items.next([]);
    this.cancelSubscription$.next();
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public setValue(value): void {
    this.control.markAsDirty();
    this.control.markAsTouched();
    this.control.setValue(value);
    this.dropdownMenuService.clearContainer();
    this.dropDownHidden();
  }

  public isValueEmpty(): boolean {
    return (
      this.control.value === this.emptyOptionValue ||
      this.control.value === undefined ||
      this.control.value === ''
    );
  }

  public dropDownHidden(): void {
    this.applyFilter('');
    this.dataSource.setSelection([this.control.value]);
  }

  public dropDownShown(): Promise<IBaseEntityType> {
    this.infoBlockError$.next(null);
    if (!this.dataSource) return;
    if (
      !this.dataSource.exhausted.value &&
      !this.dataSource.busy.value &&
      isEmpty(this.dataSource.items.value)
    ) {
      return this.dataSource.nextPage();
    }
  }

  public onScroll(event: Event): void {
    if (
      this.dataSource.exhausted.value ||
      this.dataSource.busy.value ||
      this.isModal
    ) {
      return;
    }
    const el = event.target as HTMLElement;
    const bottomReached: boolean =
      el.scrollTop + el.clientHeight + 0.5 >= el.scrollHeight;

    if (bottomReached) {
      void this.dataSource.nextPage();
    }
  }

  public async addNewItem(): Promise<void> {
    this.dropdownMenuService.clearContainer();
    this.defaultFocusElement.nativeElement.focus();
    const isImmediatelyCreation: boolean = get(
      this.field,
      ['widgetData', 'immediatelyCreateNewItem'],
      false,
    );

    if (isImmediatelyCreation) {
      return this.prepareDocumentBeforeCreation();
    }

    const createModalOptionsFn = get(this.field, [
      'widgetData',
      'createModalOptionsFn',
    ]);

    let modalOptions: IModalDialogOptions;
    if (createModalOptionsFn) {
      modalOptions = createModalOptionsFn();
    } else {
      const defaultModalOptions: IModalDialogOptions = get(this.dataSource, [
        'service',
        'formModalSettings',
      ]);
      const widgetModalOptions = get(this.field, [
        'widgetData',
        'modalOptions',
      ]);

      let formRules = Object.assign(
        {},
        get(defaultModalOptions, 'formRules'),
        get(widgetModalOptions, 'formRules', {}),
      );

      const populateFieldName: IPopulateSearchValueDefinition = get(
        widgetModalOptions,
        'populateSearchValue',
      );
      if (!isEmpty(populateFieldName)) {
        formRules = this.populateSearchValueIntoField(
          formRules,
          populateFieldName,
        );
      }

      modalOptions = Object.assign({}, { formRules }, widgetModalOptions, {
        schemaObs: this.entitySchema$,
        buttons: [
          Object.assign({}, BTN_CANCEL, {
            flexOrder: 10,
          }),
          Object.assign({}, BTN_SAVE, {
            flexOrder: -10,
          }),
        ],
      });
    }

    const { confirmed, value } =
      await this.uiActions.formModalDialog(modalOptions);

    if (!confirmed) return;

    this.dataSource.busy.next(true);
    void this.createNewItem(value, true);
    this.dataSource.busy.next(false);
  }

  public prepareDocumentBeforeCreation(): void {
    const searchValue: string = this.searchControl.value;
    const dataSourceItems = get(this.dataSource, ['items', 'value'], []);
    const itemWithSimilarName: AnyObject = find(dataSourceItems, {
      name: searchValue,
    } as any);
    const errorMessage: string =
      this.checkValidatorsForImmediatelyCreation(searchValue);

    if (!isEmpty(errorMessage)) {
      this.infoBlockError$.next({
        class: 'alert alert-warning',
        text: errorMessage,
      });

      return;
    }

    if (!isEmpty(itemWithSimilarName)) {
      this.dataSource.setSelection([itemWithSimilarName.entity_id]);
      this.setValue(itemWithSimilarName.entity_id);

      return;
    }

    const creationSchemaMapper: Function = get(this.field, [
      'widgetData',
      'creationSchemaMapper',
    ]);
    const preparedDoc: object = isFunction(creationSchemaMapper)
      ? creationSchemaMapper(searchValue)
      : searchValue;

    void this.createNewItem(preparedDoc);
  }

  public checkValidatorsForImmediatelyCreation(searchValue: string): string {
    const validators = get(
      this.field,
      ['widgetData', 'entityCreationValidators'],
      [],
    );
    const controlToValidate: FormControl = new FormControl(searchValue);
    const errorMessages: object[] = map(validators, (validator) =>
      isFunction(validator) ? validator(controlToValidate) : null,
    );
    const errorMessage: string[] = flatten(
      map(errorMessages, (data) => values(data)),
    );

    return join(errorMessage, ', ');
  }

  public adjustOrderOptionsMapper(options: SelectOption[]): SelectOption[] {
    const sortOptions: ISortOptions = get(this.field, [
      'widgetData',
      'sortOptions',
    ]);

    if (isEmpty(sortOptions)) return options;

    const { sortMapper, order = 'asc', sortByOption = 'text' } = sortOptions;

    if (isFunction(sortMapper)) {
      return orderBy(options, sortMapper, [order]);
    }

    return orderBy(options, [sortByOption], [order]);
  }

  public populateSearchValueIntoField(
    formRules: IFormRules,
    populateFieldOptions: IPopulateSearchValueDefinition,
  ): IFormRules {
    const searchValue = get(this.searchControl, 'value');
    const fieldToUpdate = get(populateFieldOptions, 'destinationFieldName');
    const fieldValueMapper = get(
      populateFieldOptions,
      'destinationValueMapper',
    );
    const mappedValue = isFunction(fieldValueMapper)
      ? fieldValueMapper(searchValue)
      : searchValue;

    if (!!mappedValue) {
      set(formRules, `fields.${fieldToUpdate}.default`, mappedValue);
    }

    return formRules;
  }

  public trackByValue(index: number, item: SelectOption): string {
    return item.value;
  }

  public ngAfterViewInit(): void {
    if (this.artificialInputCheck) {
      const artificialInputWatcherOption: IArtificialInputWatcherOption = {
        nativeElement: this.defaultFocusElement.nativeElement,
        cancelSubscription$: this.cancelSubscription$,
        artificialInputThreshold: this.artificialInputThreshold,
      };

      artificialInputWatcher(
        EInputEvent.KEYDOWN,
        {
          ...artificialInputWatcherOption,
          updateValueBeforeEventHandler: true,
          scanningService: this.scanningService,
        },
        this.setNewValue.bind(this),
      );
    }
  }

  public setNewValue(
    event: KeyboardEvent,
    artificialInputWatcherOption: IArtificialInputWatcherOption,
    newValue: string,
  ): void {
    const { updateValueBeforeEventHandler, isValidScanning } =
      artificialInputWatcherOption;

    if (updateValueBeforeEventHandler && !isValidScanning) {
      this.searchControl.setValue(newValue);
    }
    if (isValidScanning) {
      document.body.click();
    }
  }

  private async createNewItem(
    preparedDoc: object,
    extendDataSourceItems: boolean = false,
  ): Promise<void> {
    const responseData = await this.dataSource.service
      .createDocument(preparedDoc)
      .toPromise();
    const documentFromResponse = isArray(responseData)
      ? head(responseData)
      : responseData;
    this.dataSource.setSelection([documentFromResponse.entity_id]);
    this.setValue(documentFromResponse.entity_id);
    if (extendDataSourceItems) {
      this.dataSource.items.next(
        [].concat(this.dataSource.items.value, documentFromResponse),
      );
    }
    this.dataSource.purge();
    this.dataSource.nextPage();
  }

  private createDataSource(): DataSource {
    const entity = get(this.field, 'widgetData.entity', '');
    const options = Object.assign(
      {},
      get(this.field, 'widgetData.dataSourceOptions'),
      pick(this.field.widgetData, ['preloadedItems']),
    );

    return this.entityServiceProvider.createDataSource(entity, options);
  }

  private async searchControlUserInput(text): Promise<void> {
    await this.applyFilter(text);
    this.isMenuAlreadyOpen = false;
    this.displayMenu();
  }

  private applyFilter(text: string): Promise<IBaseEntityType> {
    if (this.dataSource.setFilter(text)) {
      this.dataSource.purge();
      if (this.enableClientFiltering) {
        const key = trim(text);

        return this.dataSource.getFilteringResults(
          isEmpty(key) ||
            (!isEmpty(key) && key.length < this.minClientFilteringCharLength)
            ? ''
            : key,
        );
      }

      return this.dataSource.nextPage();
    }
  }

  private prependEmptyOption(items: SelectOption[]): SelectOption[] {
    if (this.field.required) return items;
    if (this.field?.widgetData?.hideEmptyOption) return items;
    const emptyOption = {
      text: this.emptyOptionText,
      value: this.emptyOptionValue,
      selected: this.emptyOptionText && !some(items, { selected: true }),
    };

    return [emptyOption, ...items];
  }

  private hasPatternAndDoNotPassIt(text: string): boolean {
    return this.pattern && !this.pattern.test(text);
  }

  private removeCharactersDoNotPassPattern(text: string): string {
    return text
      .split('')
      .filter((a) => this.pattern.test(a))
      .join('');
  }

  private updateInfoBlockValue(): void {
    const currentInfoBlock$ = this.field.infoBlock
      ? observableMerge(
          this.infoBlockError$,
          asObservable(this.field.infoBlock),
        )
      : this.infoBlockError$;

    Object.defineProperty(this.field, 'infoBlock', {
      get(): Observable<InfoBlock> {
        return currentInfoBlock$;
      },
      set: (value: InfoBlock): void => this.infoBlockError$.next(value),
      enumerable: true,
      configurable: true,
    });
  }
}
